import { useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import { ThemeProvider, CssBaseline, Box, Typography, Alert, createTheme, GlobalStyles, alpha, lighten, darken } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { NotFound } from '@/components/NotFound';

// React Router future flags to suppress deprecation warnings
const routerFutureConfig = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
};
import { createMelpTheme } from '@melp/shared-theme';
import { LoadingIndicator } from '@/components/shared/LoadingIndicator';
import { useAuth } from '@/hooks/useAuth';
import { useEmailStore } from '@/store/emailStore';
import { JmapClient } from '@/services/jmapClient';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { usePreferences } from '@/contexts/PreferencesContext';
import { ToastProvider, useToast } from '@/contexts/ToastContext';
import { getPreferences, getBranding, fetchProfileAvatar } from '@/services/tenantClient';
import { useI18n } from './i18n';
import { ProductTourDialog } from '@/components/Help/ProductTourDialog';
import { useNotificationStream } from '@/hooks/useNotificationStream';
import LoginPage from '@/pages/LoginPage';

const Mailbox = lazy(() => import('@/pages/Mailbox').then((module) => ({ default: module.Mailbox })));
const Contacts = lazy(() => import('@/pages/Contacts').then((module) => ({ default: module.Contacts })));
const Calendar = lazy(() => import('@/pages/Calendar').then((module) => ({ default: module.Calendar })));
const CalendarEventDetail = lazy(() =>
  import('@/pages/CalendarEventDetail').then((module) => ({ default: module.CalendarEventDetail }))
);
const Drive = lazy(() => import('@/pages/Drive').then((module) => ({ default: module.Drive })));
const Settings = lazy(() => import('@/pages/Settings').then((module) => ({ default: module.Settings })));

const queryClient = new QueryClient();

const NotificationsBridge = ({ token }: { token: string | null }) => {
  const { showInfo } = useToast();
  const incrementNewMailCount = useEmailStore((state) => state.incrementNewMailCount);
  const incrementNewCalendarCount = useEmailStore((state) => state.incrementNewCalendarCount);
  const clearEmailListCache = useEmailStore((state) => state.clearEmailListCache);
  const bumpMailListRefresh = useEmailStore((state) => state.bumpMailListRefresh);

  useNotificationStream(token, {
    onMail: (payload) => {
      const lines = ['New mail'];
      if (payload.sender) {
        lines.push(`From: ${payload.sender}`);
      }
      if (payload.subject) {
        lines.push(`Subject: ${payload.subject}`);
      }
      showInfo(lines.join('\n'));
      incrementNewMailCount(1);
      clearEmailListCache();
      bumpMailListRefresh();
    },
    onCalendar: (payload) => {
      const summary = payload.summary ? `: ${payload.summary}` : '';
      if (payload.type === 'calendar.event_reminder') {
        showInfo(`Event reminder${summary}`);
        return;
      }
      showInfo(`New calendar event${summary}`);
      incrementNewCalendarCount(1);
    },
  });

  return null;
};

function App() {
  const { token, isAuthenticated, isInitialized } = useAuth();
  const { preferences, setPreferences } = usePreferences();
  const { t } = useI18n();
  const { setMailboxes, selectMailbox, selectedMailbox, mailboxes, setAccountProfile, resetStore } = useEmailStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [jmapClient, setJmapClient] = useState<JmapClient | null>(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const authWasActive = useRef(false);

  useEffect(() => {
    if (isAuthenticated) {
      authWasActive.current = true;
      return;
    }
    if (isInitialized && authWasActive.current) {
      resetStore();
      setAccountId(null);
      setJmapClient(null);
      setPrefsLoaded(false);
      setError(null);
      setLoading(false);
      authWasActive.current = false;
    }
  }, [isAuthenticated, isInitialized, resetStore, setAccountId, setError, setJmapClient, setLoading, setPrefsLoaded]);


  useEffect(() => {
    async function loadPrefs() {
      if (!token) return;

      // Prevent loading multiple times
      if (prefsLoaded) {
        console.log('[Webmail] Preferences already loaded, skipping');
        return;
      }

      console.log('[Webmail] Loading preferences and branding from server...');
      try {
        const [prefsResp, brandingResp] = await Promise.all([
          getPreferences(token),
          getBranding(token)
        ]);
        console.log('[Webmail] Preferences response:', prefsResp);
        console.log('[Webmail] Branding response:', brandingResp);

        // Check if branding response has any actual branding data
        const hasValidBranding = brandingResp && (
          brandingResp.productName ||
          brandingResp.primaryColor ||
          brandingResp.secondaryColor ||
          brandingResp.logoDataUrl ||
          brandingResp.faviconDataUrl
        );

        console.log('[Webmail] Has valid branding:', hasValidBranding);

        setPreferences({
          theme: (prefsResp.theme as any) || 'system',
          language: prefsResp.language || 'en',
          timezone: prefsResp.timezone || 'UTC',
          branding: hasValidBranding ? brandingResp : undefined
        });
        console.log('[Webmail] Preferences and branding loaded and persisted successfully');
      } catch (e) {
        // Non-fatal; fall back to local defaults (including localStorage branding)
        console.error('[Webmail] Preferences/branding fetch failed:', e);
        console.log('[Webmail] Using branding from localStorage if available');
      } finally {
        setPrefsLoaded(true);
      }
    }
    loadPrefs();
  }, [token, setPreferences, prefsLoaded]);

  const resolvedMode = useMemo(() => {
    const mode = preferences.theme;
    if (mode === 'system') {
      if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      return 'light';
    }
    return mode;
  }, [preferences.theme]);

  const themed = useMemo(() => {
    const baseTheme = createMelpTheme(resolvedMode);

    // Helper to validate hex color
    const isValidHexColor = (color: string) => /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);

    // Get brand colors with validation
    const rawPrimary = preferences.branding?.primaryColor as string;
    const rawSecondary = preferences.branding?.secondaryColor as string;
    const primary = (rawPrimary && isValidHexColor(rawPrimary)) ? rawPrimary : baseTheme.palette.primary.main;
    const secondary = (rawSecondary && isValidHexColor(rawSecondary)) ? rawSecondary : (baseTheme.palette.secondary.main || primary);

    // Generate primary color variants from brand color
    const primaryLight = lighten(primary, 0.3);
    const primaryDark = darken(primary, 0.2);

    // Generate action hover colors based on primary (subtle tint)
    const actionHover = resolvedMode === 'dark'
      ? alpha(primary, 0.08)
      : alpha(primary, 0.04);
    const actionSelected = resolvedMode === 'dark'
      ? alpha(primary, 0.16)
      : alpha(primary, 0.08);

    const brandTheme = createTheme({
      ...baseTheme,
      palette: {
        ...baseTheme.palette,
        primary: {
          ...baseTheme.palette.primary,
          main: primary,
          light: primaryLight,
          dark: primaryDark,
        },
        secondary: { ...baseTheme.palette.secondary, main: secondary },
        mode: resolvedMode,
        action: {
          ...baseTheme.palette.action,
          hover: actionHover,
          selected: actionSelected,
        },
      },
      components: {
        ...baseTheme.components,
        MuiButton: {
          ...(baseTheme.components?.MuiButton as any || {}),
          styleOverrides: {
            ...(baseTheme.components?.MuiButton?.styleOverrides as any || {}),
            root: {
              ...(baseTheme.components?.MuiButton?.styleOverrides as any)?.root || {},
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: primary,
                outlineOffset: '2px',
              },
            },
          },
        },
        MuiIconButton: {
          ...(baseTheme.components?.MuiIconButton as any || {}),
          styleOverrides: {
            ...(baseTheme.components?.MuiIconButton?.styleOverrides as any || {}),
            root: {
              ...(baseTheme.components?.MuiIconButton?.styleOverrides as any)?.root || {},
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: primary,
                outlineOffset: '2px',
              },
            },
          },
        },
        MuiListItemButton: {
          ...(baseTheme.components?.MuiListItemButton as any || {}),
          styleOverrides: {
            ...(baseTheme.components?.MuiListItemButton?.styleOverrides as any || {}),
            root: {
              ...(baseTheme.components?.MuiListItemButton?.styleOverrides as any)?.root || {},
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: primary,
                outlineOffset: '-2px',
              },
            },
          },
        },
      },
    });
    return brandTheme;
  }, [resolvedMode, preferences.branding]);

  // Apply favicon and document title when branding changes
  useEffect(() => {
    console.log('[Webmail] Branding effect triggered, branding:', preferences.branding);
    if (!preferences.branding) {
      console.log('[Webmail] No branding available, using defaults');
      return;
    }

    // Apply favicon
    if (preferences.branding.faviconDataUrl && typeof document !== 'undefined') {
      console.log('[Webmail] Applying favicon:', preferences.branding.faviconDataUrl?.substring(0, 50) + '...');
      let link: HTMLLinkElement | null = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = preferences.branding.faviconDataUrl;
    }

    // Apply product name to document title
    if (typeof document !== 'undefined') {
      const name = preferences.branding.productName || 'MelpApp Mail';
      console.log('[Webmail] Applying document title:', name);
      document.title = name;
    }

    // Log other branding properties
    if (preferences.branding.logoDataUrl) {
      console.log('[Webmail] Logo data URL available:', preferences.branding.logoDataUrl?.substring(0, 50) + '...');
    }
    if (preferences.branding.primaryColor) {
      console.log('[Webmail] Primary color:', preferences.branding.primaryColor);
    }
    if (preferences.branding.secondaryColor) {
      console.log('[Webmail] Secondary color:', preferences.branding.secondaryColor);
    }
  }, [preferences.branding]);

  // Tour trigger via global event + first-run auto-show
  useEffect(() => {
    const handler = () => setTourOpen(true);
    window.addEventListener('webmail-show-tour', handler as any);
    return () => window.removeEventListener('webmail-show-tour', handler as any);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    try {
      // Don't show tour if embedded from admin portal (token in URL)
      const urlParams = new URLSearchParams(window.location.search);
      const isEmbedded = urlParams.has('token');
      if (isEmbedded) return;

      const seen = localStorage.getItem('webmail_tour_seen');
      if (!seen) setTourOpen(true);
    } catch {}
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isInitialized) return;
    if (!isAuthenticated) {
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.history.replaceState({}, '', '/login');
      }
    }
  }, [isAuthenticated, isInitialized]);

  useEffect(() => {
    async function initializeJmap() {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const client = new JmapClient(token);
        setJmapClient(client);

        // Get JMAP session
        const sessionData = await client.getSession();

        // Get primary account ID
        const primaryAccountId = sessionData.primaryAccounts?.['urn:ietf:params:jmap:mail']
          || Object.keys(sessionData.accounts)[0];

        if (!primaryAccountId) {
          throw new Error('No mail account found in JMAP session');
        }

        setAccountId(primaryAccountId);

        // Fetch mailboxes
        const mailboxes = await client.getMailboxes(primaryAccountId);
        setMailboxes(mailboxes);

        try {
          const config = await client.getAccountConfig();
          let avatarUrl: string | null = null;
          try {
            const avatarBlob = await fetchProfileAvatar(token);
            if (avatarBlob) {
              avatarUrl = URL.createObjectURL(avatarBlob);
            }
          } catch {}
          setAccountProfile(config.email || null, config.displayName || null, avatarUrl);
        } catch {
          const username = sessionData?.username || null;
          setAccountProfile(username, null, null);
        }

        setLoading(false);
      } catch (err: any) {
        console.error('Failed to initialize JMAP:', err);
        setError(err.message || 'Failed to connect to mail server');
        setLoading(false);
      }
    }

    if (isAuthenticated && token) {
      initializeJmap();
    }
  }, [isAuthenticated, token, setMailboxes]);

  useEffect(() => {
    if (selectedMailbox) return;
    const inbox = mailboxes.find((m: any) => m.role === 'inbox');
    if (inbox) {
      selectMailbox(inbox.id);
    }
  }, [mailboxes, selectedMailbox, selectMailbox]);

  // Show loading while auth is initializing
  if (!isInitialized) {
    return <LoadingIndicator size="large" fullHeight />;
  }

  // Show webmail login screen when not authenticated
  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={themed}>
        <CssBaseline />
        <LoginPage />
      </ThemeProvider>
    );
  }

  if (loading) {
    return <LoadingIndicator size="large" fullHeight />;
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh" p={3}>
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>{t('app.connectionError.title')}</Typography>
          <Typography variant="body2">{error}</Typography>
          <Typography variant="caption" display="block" sx={{ mt: 2 }}>
            {t('app.connectionError.hint')}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={themed}>
      <QueryClientProvider client={queryClient}>
        <CssBaseline />
        <GlobalStyles styles={{
          body: {
            backgroundColor: themed.palette.background.default,
            color: themed.palette.text.primary,
            transition: 'background-color 0.2s ease, color 0.2s ease',
          },
        }} />
        <ToastProvider>
          <ErrorBoundary>
            <Router future={routerFutureConfig}>
              <Suspense fallback={<LoadingIndicator size="large" fullHeight />}>
                <Routes>
                  <Route path="/login" element={<Navigate to="/" replace />} />
                  <Route path="/" element={
                    jmapClient && accountId ? (
                      <Mailbox token={token!} accountId={accountId} jmapClient={jmapClient} />
                    ) : null
                  } />
                  <Route path="/contacts" element={<Contacts />} />
                  <Route path="/calendar" element={
                    accountId ? (
                      <Calendar token={token!} accountId={accountId} />
                    ) : null
                  } />
                  <Route path="/calendar/:eventId" element={
                    accountId ? (
                      <CalendarEventDetail token={token!} accountId={accountId} />
                    ) : null
                  } />
                  <Route path="/drive" element={
                    accountId ? (
                      <Drive token={token!} accountId={accountId} />
                    ) : null
                  } />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </Router>
            {isAuthenticated && <NotificationsBridge token={token ?? null} />}
            <ProductTourDialog open={tourOpen} onClose={() => setTourOpen(false)} />
          </ErrorBoundary>
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
